import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Icon from '@mui/material/Icon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import FolderIcon from '@mui/icons-material/Folder';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { useProject } from '../api/project';
import { useProjects } from '../api/projects';
import { useProjectFolders } from '../api/projectFolders';
import type { ProjectFolder } from '../api/projectFolders';
import { Project } from '../types/project';
import { DEFAULT_FOLDER_BOUNDARY_STYLE, resolveBoundaryStyle } from '../types/boundaryStyle';
import {
    buildFolderIndex,
    folderPath,
    normalizeFolderId,
    pathLabel,
    type FolderIndex,
    type FolderKey,
} from './projectListing/folderTree';

const INDENT_PX = 18;
const ROW_SX = { minHeight: 34, py: 0.5, gap: 1, fontSize: 14 } as const;

/** Pinned projects first (newest pin first), then by name — same rule as the listing's folders. */
const byPinThenName = (a: Project, b: Project) => {
    if (!!a.is_pinned !== !!b.is_pinned) return a.is_pinned ? -1 : 1;
    if (a.is_pinned && b.is_pinned) {
        const at = a.pinned_at ? Date.parse(a.pinned_at) : Infinity;
        const bt = b.pinned_at ? Date.parse(b.pinned_at) : Infinity;
        if (at !== bt) return bt - at;
    }
    return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }) || Number(a.id) - Number(b.id);
};

interface Tree {
    /** Active projects filed directly in each folder (null = top level). */
    projectsOf: ReadonlyMap<FolderKey, Project[]>;
    /** Active projects anywhere beneath each folder; folders with none are left out of the menu. */
    subtreeCount: ReadonlyMap<number, number>;
}

function buildTree(index: FolderIndex, projects: Project[]): Tree {
    const projectsOf = new Map<FolderKey, Project[]>();
    projects.forEach((p) => {
        if (p.is_archived) return;
        const key = normalizeFolderId(index, p.folder);
        const list = projectsOf.get(key);
        if (list) list.push(p);
        else projectsOf.set(key, [p]);
    });
    projectsOf.forEach((list) => list.sort(byPinThenName));

    const subtreeCount = new Map<number, number>();
    const count = (id: number): number => {
        const cached = subtreeCount.get(id);
        if (cached !== undefined) return cached;
        let n = projectsOf.get(id)?.length ?? 0;
        (index.childrenOf.get(id) ?? []).forEach((child) => {
            n += count(child.id);
        });
        subtreeCount.set(id, n);
        return n;
    };
    index.byId.forEach((f) => count(f.id));
    return { projectsOf, subtreeCount };
}

export default function ProjectsDropdown() {
    const [ selectedProject, setSelectedProject ] = useState<Project | null>(null);
    const { project } = useProject(); // Access project data from the query client

    const { projects, isLoading, error } = useProjects();
    const { folders } = useProjectFolders();
    const [ anchorEl, setAnchorEl ] = useState<HTMLElement | null>(null);
    const [ expanded, setExpanded ] = useState<Set<number>>(() => new Set());

    const navigate = useNavigate();

    const index = useMemo(() => buildFolderIndex(folders), [folders]);
    const tree = useMemo(() => buildTree(index, projects?.data ?? []), [index, projects]);

    // The folders above the current project: expanded when the menu opens so
    // the project the person is on is in view, and shown as its location.
    const currentFolder = normalizeFolderId(index, selectedProject?.folder);
    const currentPath = useMemo(() => folderPath(index, currentFolder), [index, currentFolder]);
    const currentPathLabel = pathLabel(index, currentFolder);

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setExpanded(new Set(currentPath.map((f) => f.id)));
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleProjectSelect = (project: Project) => {
        handleClose();
        setSelectedProject(project);
        navigate(`/projects/${project.id}`);
        window.location.reload()
    };

    const toggleFolder = (id: number) => {
        setExpanded((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    useEffect(() => {
        if (project) {
            setSelectedProject(project.data);
        }
    }, [project]);

    // Keep selected project in sync when projects list updates (e.g., after a rename or a move)
    useEffect(() => {
        if (selectedProject && projects?.data) {
            // Find the current project in the updated projects list
            const updatedProject = projects.data.find(p => p.id === selectedProject.id);
            if (
                updatedProject &&
                (updatedProject.name !== selectedProject.name || updatedProject.folder !== selectedProject.folder)
            ) {
                // Update the selected project with fresh data
                setSelectedProject(updatedProject);
            }
        }
    }, [projects, selectedProject]);


    const renderProjectMenuItem = (project: Project, depth: number) => {
        const isCurrent = selectedProject?.id === project.id;
        return (
            <MenuItem
                key={`p-${project.id}`}
                selected={isCurrent}
                onClick={() => handleProjectSelect(project)}
                sx={{
                    ...ROW_SX,
                    pl: `${12 + depth * INDENT_PX}px`,
                    '&.Mui-selected': { bgcolor: 'rgba(0, 212, 173, 0.18)' },
                    '&.Mui-selected:hover': { bgcolor: 'rgba(0, 212, 173, 0.26)' },
                }}
            >
                {/* Keeps project names aligned with folder names at the same depth. */}
                <Box sx={{ width: 20, flexShrink: 0 }} />
                <Typography noWrap sx={{ fontSize: 'inherit', fontWeight: isCurrent ? 600 : 400 }}>
                    {project.name}
                </Typography>
            </MenuItem>
        );
    };

    // Rows come back as a flat array: MUI's Menu wants plain children (a
    // Fragment breaks its keyboard focus handling), so nothing is nested here.
    const renderFolder = (folder: ProjectFolder, depth: number): React.ReactNode[] => {
        const total = tree.subtreeCount.get(folder.id) ?? 0;
        if (total === 0) return []; // nothing to switch to in there
        const isOpen = expanded.has(folder.id);
        const onPath = currentPath.some((f) => f.id === folder.id);
        const colour = resolveBoundaryStyle(folder.boundary_style, DEFAULT_FOLDER_BOUNDARY_STYLE).color;
        const Chevron = isOpen ? ExpandMoreIcon : ChevronRightIcon;
        const Glyph = isOpen ? FolderOpenIcon : FolderIcon;
        return [
            <MenuItem
                key={`f-${folder.id}`}
                onClick={() => toggleFolder(folder.id)}
                aria-expanded={isOpen}
                sx={{ ...ROW_SX, pl: `${12 + depth * INDENT_PX}px` }}
            >
                <Chevron sx={{ fontSize: 20, color: 'rgba(255,255,255,0.6)', flexShrink: 0 }} />
                <Glyph sx={{ fontSize: 20, color: colour, flexShrink: 0 }} />
                <Typography noWrap sx={{ fontSize: 'inherit', fontWeight: onPath ? 600 : 400, flex: 1 }}>
                    {folder.name}
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.55)', ml: 1, flexShrink: 0 }}>
                    {total}
                </Typography>
            </MenuItem>,
            ...(isOpen ? renderLevel(folder.id, depth + 1) : []),
        ];
    };

    /** Sub-folders first, then the projects filed directly here — the listing's order. */
    const renderLevel = (key: FolderKey, depth: number): React.ReactNode[] => [
        ...(index.childrenOf.get(key) ?? []).flatMap((f) => renderFolder(f, depth)),
        ...(tree.projectsOf.get(key) ?? []).map((p) => renderProjectMenuItem(p, depth)),
    ];

    const hasProjects = (projects?.data ?? []).some((p) => !p.is_archived);

    return (
        <div>
            <Tooltip title={currentPathLabel ? `In ${currentPathLabel}` : ''} arrow enterDelay={600}>
                <Button
                    onClick={handleClick}
                    sx = {{
                        color: 'white',
                        textTransform: 'none',
                        "&:focus": {
                            outline: "none", // Remove the focus outline/border
                            boxShadow: "none", // Remove any focus shadow
                        },
                    }}
                >
                    <Typography noWrap component="span" sx={{ display: 'block', font: 'inherit', maxWidth: 320 }}>
                        {selectedProject ? selectedProject.name : 'Project'}
                    </Typography>
                    <Icon sx={{ color: '#fff'}}>expand_more</Icon>
                </Button>
            </Tooltip>
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                sx={{
                    top: -5,
                    '& .MuiMenu-paper': {
                        background: '#212b37',
                        color: 'white',
                        minWidth: 280,
                        maxWidth: 440,
                        maxHeight: 'min(70vh, 560px)',
                    }
                }}
            >
                {isLoading && <MenuItem disabled>Loading...</MenuItem>}
                {error && <MenuItem disabled>Error: {error.message}</MenuItem>}
                {!isLoading && !error && !hasProjects && <MenuItem disabled>No projects</MenuItem>}
                {renderLevel(null, 0)}
            </Menu>
        </div>
    );
}
